import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Storage scaffold.
 *
 * This is the plumbing for future document intake, deliberately stopping short
 * of any document semantics: no receipts, no extraction, no classification.
 * What it does establish, and what everything built later depends on, is:
 *
 *  - files are addressed by an organisation-scoped key, so a storage policy can
 *    enforce tenancy the same way RLS does for tables;
 *  - uploads go directly from browser to Supabase Storage via a short-lived
 *    signed URL, so document bytes never transit the API;
 *  - both authorisation steps are audited.
 */
export const STORAGE_BUCKETS = {
    /** Original, immutable financial documents. Never overwritten, never deleted. */
    DOCUMENTS: 'documents',
};
export const storageBucketSchema = z.enum(Object.values(STORAGE_BUCKETS));
/** Content types accepted at the perimeter. Widened deliberately, never by accident. */
export const ALLOWED_UPLOAD_CONTENT_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/webp',
    'text/csv',
];
export const uploadContentTypeSchema = z.enum(ALLOWED_UPLOAD_CONTENT_TYPES);
/** 25 MB. A scanned multi-page supplier statement fits comfortably. */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
export const createUploadUrlRequestSchema = z.object({
    bucket: storageBucketSchema.default('documents'),
    /** Original filename, used only for the stored object's display metadata. */
    filename: z
        .string()
        .trim()
        .min(1)
        .max(255)
        // Path separators and traversal sequences are rejected outright rather than
        // sanitised, so there is no clever encoding that slips through.
        .refine((value) => !value.includes('/') && !value.includes('\\') && !value.includes('..'), {
        message: 'filename must not contain path separators',
    }),
    contentType: uploadContentTypeSchema,
    sizeBytes: z.number().int().positive().max(MAX_UPLOAD_BYTES),
});
export const uploadTicketSchema = z.object({
    /** Server-assigned object key: `<organizationId>/<yyyy>/<mm>/<uuid><ext>`. */
    objectKey: z.string().min(1),
    bucket: storageBucketSchema,
    /** Signed URL the browser PUTs to. Short-lived. */
    uploadUrl: z.url(),
    /** Token Supabase requires alongside the signed upload. */
    token: z.string().min(1),
    expiresAt: isoDateTimeSchema,
});
export const createDownloadUrlRequestSchema = z.object({
    bucket: storageBucketSchema.default('documents'),
    objectKey: z.string().min(1),
    /** Seconds the link stays valid. Capped server-side. */
    expiresInSeconds: z.number().int().min(30).max(3600).default(300),
});
export const downloadTicketSchema = z.object({
    objectKey: z.string().min(1),
    downloadUrl: z.url(),
    expiresAt: isoDateTimeSchema,
});
export const storageObjectSchema = z.object({
    id: uuidSchema,
    organizationId: uuidSchema,
    bucket: storageBucketSchema,
    objectKey: z.string(),
    filename: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int(),
    /** SHA-256 of the bytes, set once the upload is confirmed. */
    checksumSha256: z.string().nullable(),
    uploadedBy: uuidSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
//# sourceMappingURL=storage.js.map